import { useEffect, useRef, useState } from 'react';
import { Copy, Check, ExternalLink, Globe, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/hooks/useTenant';
import { toast } from 'sonner';

const PORTAL_BASE_URL = 'https://app.hermys.ai/portal';

export const PortalClientePageComponent = () => {
  const { tenant, loading } = useTenant();
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!tenant?.portal_code) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Não foi possível gerar o link do portal. Entre em contato com o suporte.
        </CardContent>
      </Card>
    );
  }

  const portalUrl = `${PORTAL_BASE_URL}?t=${tenant.portal_code}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      toast.success('Link copiado!');
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleOpen = () => {
    window.open(portalUrl, '_blank', 'noopener');
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="p-4 sm:p-6 pb-3">
        <CardTitle className="text-sm sm:text-base font-medium flex items-center gap-2">
          <div className="flex items-center justify-center h-6 w-6 rounded-md bg-amber-50 text-amber-600">
            <Globe className="h-3.5 w-3.5" />
          </div>
          Link do Portal do Cliente
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Compartilhe com seus clientes para que consultem pedidos e rastreios pelo CPF.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 space-y-3">
        <div className="flex gap-2">
          <Input value={portalUrl} readOnly className="bg-gray-50 text-sm" />
          <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={handleOpen} className="shrink-0">
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Enquanto o plugin estiver ativo, clientes com CPF cadastrado conseguem visualizar compras passadas e status de envios nesse link.
        </p>
      </CardContent>
    </Card>
  );
};
